"use client";

import { useState } from "react";
import Link from "next/link";
import { KeyRound, Loader2, CheckCircle } from "lucide-react";

const API = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

export default function ForgotPasswordPage() {
    const [login, setLogin] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [resetToken, setResetToken] = useState("");
    const [done, setDone] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const res = await fetch(`${API}/api/auth/forgot-password/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ login: login.trim() }),
            });
            const data = await res.json();
            if (!res.ok) {
                setError(data.detail || data.non_field_errors?.[0] || "حدث خطأ. حاول مجدداً.");
                return;
            }
            setResetToken(data.reset_token || "");
            setDone(true);
        } catch {
            setError("خطأ في الاتصال بالخادم.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center p-4">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 -left-24 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 -right-24 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl" />
            </div>

            <div className="relative w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-600 rounded-3xl shadow-2xl shadow-indigo-900/50 mb-4">
                        <KeyRound className="h-10 w-10 text-white" />
                    </div>
                    <h1 className="text-3xl font-black text-white">نسيت كلمة المرور</h1>
                    <p className="text-indigo-300 text-sm mt-1">سنساعدك على استعادة حسابك</p>
                </div>

                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
                    {!done ? (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <p className="text-white/60 text-sm text-center">
                                أدخل رقم جوالك أو بريدك الإلكتروني المرتبط بحسابك
                            </p>

                            <div>
                                <label className="block text-sm font-medium text-indigo-200 mb-1.5">
                                    رقم الجوال أو البريد الإلكتروني
                                </label>
                                <input
                                    type="text"
                                    value={login}
                                    onChange={(e) => setLogin(e.target.value)}
                                    required
                                    dir="ltr"
                                    className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition"
                                    placeholder="+966501234567 أو example@email.com"
                                />
                            </div>

                            {error && (
                                <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-3 text-red-300 text-sm text-center">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-800 disabled:cursor-not-allowed text-white font-bold rounded-2xl py-3.5 text-sm transition flex items-center justify-center gap-2 shadow-lg shadow-indigo-900/50"
                            >
                                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                                {loading ? "جاري الإرسال..." : "إرسال رمز الاسترداد"}
                            </button>

                            <p className="text-center text-white/40 text-xs">
                                <Link href="/login" className="text-indigo-400 hover:underline">
                                    ← العودة لتسجيل الدخول
                                </Link>
                            </p>
                        </form>
                    ) : (
                        <div className="space-y-6 text-center">
                            <CheckCircle className="h-16 w-16 text-green-400 mx-auto animate-bounce" />
                            <h3 className="text-white font-black text-xl">تم إرسال الرمز</h3>
                            <p className="text-white/60 text-sm">
                                إذا كان الحساب موجوداً، سيصلك رمز إعادة التعيين على جوالك أو بريدك.
                            </p>

                            {resetToken && (
                                <div className="bg-white/10 border border-white/20 rounded-2xl p-4">
                                    <p className="text-xs text-white/50 mb-2">رمز الاسترداد (للعرض التجريبي):</p>
                                    <code className="text-orange-400 font-mono text-sm break-all">{resetToken}</code>
                                </div>
                            )}

                            <Link
                                href={`/reset-password${resetToken ? `?token=${resetToken}` : ""}`}
                                className="block w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl py-3.5 text-sm transition text-center shadow-lg shadow-indigo-900/50"
                            >
                                إعادة تعيين كلمة المرور →
                            </Link>

                            <p className="text-center text-white/40 text-xs">
                                <Link href="/login" className="text-indigo-400 hover:underline">
                                    ← العودة لتسجيل الدخول
                                </Link>
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
